import { Formik, type FormikHelpers, type FormikProps } from 'formik';
import { useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button, Form } from 'react-bootstrap';
import Select, { type SingleValue } from 'react-select';
import * as yup from 'yup';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import {
  useAddProviderKeyMutation,
  useGetProvidersQuery,
  useLazyGetProviderModelsQuery
} from '@/app/Settings/api/aiSettingsApi';
import type { ProviderOption } from '@/app/Settings/types/aiSettingsApiTypes';
import { errorMessage, successMessage } from '@/utils/toast';

type AddApiKeyFormValues = {
  key_name: string;
  provider: string;
  model: string;
  api_key: string;
  description: string;
  is_active: boolean;
};

type AddAIApiKeyModalProps = {
  show: boolean;
  onHide: () => void;
  onSubmit?: (createdProviderKey: string) => void;
  isSubmitting?: boolean;
};

type ApiErrorShape = {
  data?: { msg?: string };
};

const addApiKeySchema = yup.object().shape({
  key_name: yup.string().required('نام کلید الزامی است'),
  provider: yup.string().required('ارائه دهنده الزامی است'),
  model: yup.string().required('مدل الزامی است'),
  api_key: yup.string().required('مقدار API Key الزامی است'),
  description: yup.string()
});

const initialValues: AddApiKeyFormValues = {
  key_name: '',
  provider: '',
  model: '',
  api_key: '',
  description: '',
  is_active: true
};

const AddAIApiKeyModal = ({
  show,
  onHide,
  onSubmit,
  isSubmitting = false
}: AddAIApiKeyModalProps) => {
  const formikRef = useRef<FormikProps<AddApiKeyFormValues>>(null);
  const [addProviderKey, { isLoading: isAddingProviderKey }] =
    useAddProviderKeyMutation();
  const { data: providersData, isFetching: isFetchingProviders } =
    useGetProvidersQuery();
  const [getProviderModels, { data: modelsData, isFetching: isFetchingModels }] =
    useLazyGetProviderModelsQuery();
  const providers = useMemo(
    () => (Array.isArray(providersData) ? providersData : []),
    [providersData]
  );
  const models = useMemo(
    () => (Array.isArray(modelsData) ? modelsData : []),
    [modelsData]
  );

  useEffect(() => {
    if (!show || providers.length === 0 || !formikRef.current) return;
    if (formikRef.current.values.provider) return;

    const defaultProvider = providers[0];
    formikRef.current.setFieldValue('provider', defaultProvider.value, false);
    formikRef.current.setFieldValue('model', '', false);
    getProviderModels(defaultProvider.value);
  }, [show, providers, getProviderModels]);

  useEffect(() => {
    if (!show || models.length === 0 || !formikRef.current) return;
    if (formikRef.current.values.model) return;

    const defaultModel = models[0];
    formikRef.current.setFieldValue('model', defaultModel.value, false);
  }, [show, models]);

  const handleFormSubmit = async (
    values: AddApiKeyFormValues,
    actions: FormikHelpers<AddApiKeyFormValues>
  ) => {
    try {
      const payload = {
        provider_id: values.provider,
        model_id: values.model,
        encrypted_api_key: values.api_key.trim(),
        ...(values.key_name?.trim()
          ? { key_name: values.key_name.trim() }
          : {})
      };

      const createdProviderKey = await addProviderKey(payload).unwrap();
      successMessage('API Key با موفقیت اضافه شد');
      onSubmit?.(createdProviderKey);
      actions.resetForm();
    } catch (err) {
      const error = err as ApiErrorShape;
      errorMessage(error?.data?.msg || 'خطا در افزودن API Key');
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Formik<AddApiKeyFormValues>
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={addApiKeySchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting: isFormikSubmitting
        }) => (
          <Form
            autoComplete="off"
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title className="fs-1">افزودن API Key جدید</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>نام کلید</Form.Label>
                <Form.Control
                  type="text"
                  name="key_name"
                  value={values.key_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.key_name && !!errors.key_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.key_name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>ارائه دهنده</Form.Label>
                {isFetchingProviders ? (
                  <div style={{ margin: '4px 0px' }}>
                    <TextLoading height={38} width={'100%'} />
                  </div>
                ) : (
                  <Select
                    name="ai_provider_select"
                    inputId="ai-provider-select"
                    autoComplete="new-password"
                    menuShouldScrollIntoView
                    closeMenuOnSelect
                    isSearchable={false}
                    options={providers}
                    classNamePrefix="react-select"
                    placeholder="انتخاب ارائه دهنده"
                    value={
                      providers.find(
                        provider => provider.value === values.provider
                      ) ?? null
                    }
                    onChange={(
                      selectedOption: SingleValue<ProviderOption>
                    ) => {
                      const providerValue = selectedOption?.value ?? '';
                      setFieldValue('provider', providerValue);
                      setFieldValue('model', '');
                      if (providerValue) {
                        getProviderModels(providerValue);
                      }
                    }}
                    onBlur={() => setFieldTouched('provider', true)}
                    isDisabled={providers.length === 0}
                    maxMenuHeight={300}
                  />
                )}
                {touched.provider && errors.provider ? (
                  <div className="invalid-feedback d-block">
                    {errors.provider}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>مدل</Form.Label>
                {values.provider && isFetchingModels ? (
                  <div style={{ margin: '4px 0px' }}>
                    <TextLoading height={38} width={'100%'} />
                  </div>
                ) : (
                  <Select
                    name="ai_model_select"
                    inputId="ai-model-select"
                    autoComplete="new-password"
                    menuShouldScrollIntoView
                    closeMenuOnSelect
                    isSearchable={false}
                    options={models}
                    classNamePrefix="react-select"
                    placeholder={
                      values.provider
                        ? 'انتخاب مدل'
                        : 'ابتدا ارائه دهنده را انتخاب کنید'
                    }
                    value={
                      models.find(model => model.value === values.model) ?? null
                    }
                    onChange={selectedOption => {
                      setFieldValue('model', selectedOption?.value ?? '');
                    }}
                    onBlur={() => setFieldTouched('model', true)}
                    isDisabled={!values.provider || models.length === 0}
                    maxMenuHeight={300}
                    noOptionsMessage={() => 'مدلی یافت نشد'}
                  />
                )}
                {touched.model && errors.model ? (
                  <div className="invalid-feedback d-block">{errors.model}</div>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>API Key</Form.Label>
                <Form.Control
                  type="text"
                  name="api_key"
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  value={values.api_key}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.api_key && !!errors.api_key}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.api_key}
                </Form.Control.Feedback>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                انصراف
              </Button>
              <Button
                type="submit"
                variant="falcon-primary"
                disabled={
                  isSubmitting || isFormikSubmitting || isAddingProviderKey
                }
              >
                افزودن
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddAIApiKeyModal;
